// pages/HelpDesk.tsx
import React from "react";
import { BsChat } from "react-icons/bs";
import Button from "../components/Button";
import DataTableView from "../components/DataTableView2";
import ChatModal from "../components/HelpDesk/ChatModal";
import ChatModalDesktop from "../components/HelpDesk/ChatModalDesktop";
import ViewDetails, { ComplaintType } from "../components/HelpDesk/ViewDetails";
import Input from "../components/Input";
import Select from "../components/Select";
import { TruncatedText } from "../components/truncated";
import { capitalize } from "../utils/helpers";
import { IDriverTypeResponse, IUserTypeResponse } from "../utils/types";
import useHelpDesk from '../hooks/logics/suppport/use-help-desk-dashboard';

type ApiResponse = IUserTypeResponse | IDriverTypeResponse;

const HelpDesk = () => {
  const {
    data,
    totalPages,
    isFetching,
    currentPage,
    setCurrentPage,
    userType,
    setUserType,
    search,
    setSearch,
    selectedComplaint,
    isChatVisible,
    isDetailsVisible,
    handleSearch,
    handleClear,
    handleOpenChat,
    handleOpenDetails,
    handleCloseChat,
    handleCloseDetails,
    onMessageRead,
  } = useHelpDesk();

  const isDesktop = window.innerWidth > 850;

  const columns = [
    {
      header: "User",
      className: "md:p-0 md:pl-10",
      accessor: (item: ApiResponse) => (
        <div className="md:!pl-7">
          {item?.userType === "user"
            ? item?.reportingUser?.fullName || "N/A"
            : item?.reportingDriver?.fullName || "N/A"}
        </div>
      ),
    },
    {
      header: "Title",
      accessor: (item: ApiResponse) => (
        <TruncatedText className="break-words" text={item?.title || "N/A"} limit={20} />
      ),
    },
    {
      header: "Type",
      accessor: (item: ApiResponse) => (
        <div className="md:w-[100px]">{item.userType ? capitalize(item.userType) : "N/A"}</div>
      ),
    },
    {
      header: "Description",
      accessor: (item: ApiResponse) => (
        <TruncatedText text={item?.description || "N/A"} limit={20} />
      ),
    },
    {
      header: "Action",
      accessor: (item: ApiResponse) => (
        <div className="flex flex-col items-start md:flex-row md:items-center md:gap-10 ml-1 -mt-1.5 md:mt-0">
          <div className="flex items-center justify-start gap-3 md:gap-10">
            <Button
              variant="outline"
              onClick={() => handleOpenChat(item)}
              className="relative border-none w-10 h-10 flex items-center justify-center text-lg"
            >
              {item?.unreadCount > 0 && (
                <div className="absolute top-[0.075rem] right-[-0.075rem] bg-primary min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] flex items-center justify-center">
                  {item?.unreadCount}
                </div>
              )}
              <BsChat className="text-xl" />
            </Button>

            {item._id && (
              <button onClick={() => handleOpenDetails(item)} className="text-primary">
                Details
              </button>
            )}

            {!item._id && !item.serviceId && <p>N/A</p>}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="w-full h-full">
        <div className="pb-4 flex flex-col justify-center font-inter bg-gray-100">
          <form
            onSubmit={handleSearch}
            className="flex flex-col md:flex-row gap-2 md:gap-4 items-center md:items-end justify-end mb-4 space-y-2"
          >
            <div className="flex w-full gap-2 md:gap-4 items-center md:items-end justify-end">
              <Input
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                containerClassName="w-2/3 h-full md:w-auto"
                outerClassName="h-full"
                className="w-full md:w-52 outline-none input-primary h-8 md:h-10 rounded-lg"
              />
              <Select
                value={userType}
                onChange={(e) => { setCurrentPage(1); setUserType(e.target.value); }}
                placeholder="User type"
                className="md:w-32 outline-none bg-white rounded-lg min-h-0 h-8 md:h-10 pl-4 pr-2"
                containerClassName="w-1/2 md:w-auto"
                options={[
                  { label: "User", value: "user" },
                  { label: "Driver", value: "driver" },
                ]}
              />
            </div>
            <div className="w-full md:w-min flex flex-row gap-x-2 md:gap-x-4 md:justify-end">
              <Button
                disabled={search === "" && userType === ""}
                className="flex-1 md:flex-none md:w-32 min-h-0 h-8 md:h-10"
                type="submit"
              >
                Search
              </Button>
              <Button
                disabled={userType === "" && search === ""}
                type="button"
                className="flex-1 md:flex-none md:w-24 min-h-0 h-8 md:h-10"
                onClick={handleClear}
              >
                Clear
              </Button>
            </div>
          </form>
        </div>

        <DataTableView
          data={data}
          columns={columns}
          isFetching={isFetching}
          emptyStateMessage="No data found"
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
        />
      </div>

      <ViewDetails
        onMessageRead={onMessageRead}
        isVisible={isDetailsVisible}
        onHide={handleCloseDetails}
        id={selectedComplaint?._id || ""}
      />

      {isDesktop ? (
        <ChatModalDesktop
          onMessageRead={onMessageRead}
          complaint={selectedComplaint as ComplaintType}
          isVisible={isChatVisible}
          onHide={handleCloseChat}
        />
      ) : (
        <ChatModal
          onMessageRead={onMessageRead}
          complaint={selectedComplaint as ComplaintType}
          isVisible={isChatVisible}
          onHide={handleCloseChat}
        />
      )}
    </>
  );
};

export default HelpDesk;